import React, { useState, useEffect, useRef } from 'react';
import { getCart, setQty, addToCart, applyCoupon, removeCoupon, cartShipping, selectShipping } from './api';
import CrossSells from './CrossSells';

const cfg = (typeof window !== 'undefined' && window.CART_NEXT) || {};

// PostHog capture that can never break the cart (no-op when the SDK is absent or
// blocked by the cookie banner). Every cart event carries location=cart_next so the
// dashboards can separate the React cart from the classic one.
const track = (event, props) => {
  try {
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(event, { location: 'cart_next', ...props });
    }
  } catch (e) { /* analytics must never throw into the UI */ }
};

// Format a Greek 5-digit postcode as "XXX XX"; otherwise return the digits as-is.
const prettyZip = (zip) => {
  const d = String(zip || '').replace(/\D/g, '');
  return d.length === 5 ? `${d.slice(0, 3)} ${d.slice(3)}` : d;
};

// Shipping-group icons — courier (truck), boxnow (locker), pickup (store).
const ShipIcon = ({ id }) => {
  const p = { width: 26, height: 26, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  if (id === 'boxnow') {
    return (<svg {...p}><rect x="4" y="2" width="16" height="20" rx="1.5" /><line x1="4" y1="8" x2="20" y2="8" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="9" y1="5" x2="10.5" y2="5" /><line x1="9" y1="11.5" x2="10.5" y2="11.5" /><line x1="9" y1="18.5" x2="10.5" y2="18.5" /></svg>);
  }
  if (id === 'pickup') {
    return (<svg {...p}><path d="M3 9l1.5-5h15L21 9M4 9v11h16V9M4 9h16" /><path d="M3 9a2.4 2.4 0 004.5 0 2.4 2.4 0 004.5 0 2.4 2.4 0 004.5 0 2.4 2.4 0 004.5 0" /></svg>);
  }
  return (<svg {...p}><path d="M1 3h13v11H1zM14 7h4l3 3v4h-7z" /><circle cx="6" cy="18" r="2" /><circle cx="17.5" cy="18" r="2" /></svg>);
};

// Outcome of an AutomateWoo abandoned-cart link (/cart/?aw-action=restore-cart).
// Only 'fail' really needs saying: the customer clicked "your cart is waiting" and,
// without this, lands on a bare empty cart that reads as if the email lied. Rendered
// server-side into cfg so it survives the classic-cart redirect.
const RestoreNotice = () => {
  const state = cfg.cartRestored;
  if (state !== 'success' && state !== 'fail') return null;
  if (state === 'success') {
    return (
      <div className="cart-restored" role="status">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
        <span>Επαναφέραμε το καλάθι σου. Οι τιμές και η διαθεσιμότητα είναι οι σημερινές.</span>
      </div>
    );
  }
  return (
    <div className="cart-restored is-fail" role="alert">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
      <span>Ο σύνδεσμος επαναφοράς του καλαθιού έχει λήξει, οπότε δεν μπορέσαμε να φέρουμε πίσω τα προϊόντα σου.</span>
    </div>
  );
};

// React cart page (admin-gated /cart-next): live line items, BoxNow free-shipping
// teaser, cross-sells, a shipping-group selector with estimates, delivery ETA,
// coupon, totals, and continue-to-checkout.
export default function CartNext() {
  const [cart, setCart] = useState(cfg.cart || null);
  const [crossSells, setCrossSells] = useState(cfg.crossSells || []);
  const [similarCases, setSimilarCases] = useState(cfg.similarCases || []);
  const [cameraProtectors, setCameraProtectors] = useState(cfg.cameraProtectors || []);
  const [magsafe, setMagsafe] = useState(cfg.magsafe || []);
  const [upsells, setUpsells] = useState(cfg.upsells || []);
  const [otherBrands, setOtherBrands] = useState(cfg.otherBrands || []);
  const [freqBought, setFreqBought] = useState(cfg.freqBought || []);
  const [phoneAccessories, setPhoneAccessories] = useState(cfg.phoneAccessories || []);
  const [promoMeta, setPromoMeta] = useState(cfg.promoMeta || {});
  const [shipping, setShipping] = useState(cfg.shipping || null);
  const [delivery, setDelivery] = useState(cfg.delivery || null);
  const [syncing, setSyncing] = useState(0); // in-flight request count → non-blocking loader
  const [pending, setPending] = useState({}); // key -> optimistic qty while debouncing
  const [done, setDone] = useState({}); // key -> show a success tick briefly after sync
  const doneTimers = useRef({});
  const timers = useRef({});
  const latest = useRef({}); // key -> newest requested qty (guards overlapping flushes)
  const pcRef = useRef('');
  const [postcode, setPostcode] = useState((cfg.shipping && cfg.shipping.postcode) || '');
  pcRef.current = postcode;
  const [coupon, setCoupon] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponErr, setCouponErr] = useState('');
  const [zipOpen, setZipOpen] = useState(false);
  const [adding, setAdding] = useState({}); // cross-sell id -> add-to-cart in flight
  const [chosenGroup, setChosenGroup] = useState(''); // no shipping preselected — user must pick
  const [shipErr, setShipErr] = useState(''); // "choose a method" validation on proceed
  const [shipPopup, setShipPopup] = useState(false); // mobile: pick shipping in a popup on proceed
  const [couponOpen, setCouponOpen] = useState(false);
  // Per-item transient validation message, shown at the bottom of that cart line.
  const [itemMsg, setItemMsg] = useState({}); // key -> message
  const msgTimers = useRef({});
  const flashItemMsg = (key, msg) => {
    if (!key || !msg) return;
    setItemMsg((m) => ({ ...m, [key]: msg }));
    clearTimeout(msgTimers.current[key]);
    msgTimers.current[key] = setTimeout(() => setItemMsg((m) => { const n = { ...m }; delete n[key]; return n; }), 5000);
  };
  // Message when the user hits an item's quantity cap. cap === per-code limit (cart.qtyCap,
  // set on Κανόνες & Όρια) → per-order rule; a smaller cap → limited supplier stock.
  const qtyCap = (cart && cart.qtyCap) || 20;
  const capMessage = (cap) => (cap >= qtyCap
    ? `Μπορείς να προσθέσεις μέχρι ${qtyCap} τεμάχια ανά προϊόν.`
    : `Διαθέσιμα μόνο ${cap} τεμάχια αυτή τη στιγμή.`);

  const apply = (data) => {
    if (data.cart) {
      setCart(data.cart);
      // Drop any optimistic pending qty the authoritative cart already satisfies, so a
      // flush that was superseded (its response not applied) doesn't leave a stale
      // pending lingering once the latest cumulative cart lands.
      setPending((p) => {
        const ks = Object.keys(p);
        if (!ks.length) return p;
        const q = {};
        (data.cart.items || []).forEach((it) => { q[it.key] = it.qty; });
        let changed = false;
        const n = { ...p };
        ks.forEach((k) => { if (q[k] != null && String(q[k]) === String(n[k])) { delete n[k]; changed = true; } });
        return changed ? n : p;
      });
    }
    if (data.shipping) setShipping(data.shipping);
    if (data.delivery !== undefined) setDelivery(data.delivery);
    if (data.crossSells) setCrossSells(data.crossSells);
    if (data.cameraProtectors) setCameraProtectors(data.cameraProtectors);
    if (data.magsafe) setMagsafe(data.magsafe);
    if (data.upsells) setUpsells(data.upsells);
    if (data.otherBrands) setOtherBrands(data.otherBrands);
    if (data.similarCases) setSimilarCases(data.similarCases);
    if (data.freqBought) setFreqBought(data.freqBought);
    if (data.phoneAccessories) setPhoneAccessories(data.phoneAccessories);
    if (data.promoMeta) setPromoMeta(data.promoMeta);
    // The server skips the (slow) suggestion rebuild on cart mutations — pick the
    // fresh set up in the background once the shopper stops tapping.
    if (data.promosStale) refreshPromos();
  };

  // SERIALIZE cart mutations. Rapid deletes/qty-changes must not run concurrently:
  // the server can process them out of order, so a later request's response may still
  // list items an earlier delete removed — and applying it makes removed rows reappear.
  // We chain each mutation's NETWORK op after the previous one (so the last response
  // always reflects every change), while the seq guard applies only the latest result.
  // Optimistic UI (row removal etc.) happens synchronously in the handlers, so the cart
  // still feels instant even though the requests run one-at-a-time in the background.
  const mutSeq = useRef(0);
  const chain = useRef(Promise.resolve());
  const applyFresh = (seq, data) => { if (seq === mutSeq.current) apply(data); };

  const run = (fn) => {
    const seq = ++mutSeq.current;
    setSyncing((s) => s + 1); // count from ENQUEUE so "busy" spans the whole queued burst (no flicker between chained ops)
    const p = chain.current.then(() => fn(seq)).finally(() => setSyncing((s) => s - 1));
    chain.current = p.catch(() => {}); // keep the chain alive even if one op fails
    return p;
  };

  // Hydrate from the authoritative session cart on mount (the localized snapshot
  // can be empty depending on when WC loaded the cart during the page render).
  // This request is NOT in the mutation chain, so a delete fired while it's still
  // in flight would otherwise be undone by the (pre-delete) hydrate response and
  // the row would pop back. Any mutation started meanwhile wins — drop the hydrate.
  useEffect(() => {
    let cancelled = false;
    const seq0 = mutSeq.current;
    getCart(postcode).then(({ ok, data }) => {
      if (ok && !cancelled && mutSeq.current === seq0) apply(data);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deletes skip the server-side cross-sell rebuild (see cnc_qty) to stay snappy.
  // Once the deleting stops, refresh the suggestions off the critical path so a
  // removed case can't leave its (now unearned) discounted protectors on screen.
  const promoTimer = useRef(null);
  const refreshPromos = () => {
    clearTimeout(promoTimer.current);
    promoTimer.current = setTimeout(() => {
      run(async (seq) => {
        const { ok, data } = await getCart(pcRef.current);
        if (ok) applyFresh(seq, data);
      });
    }, 1200);
  };
  useEffect(() => () => clearTimeout(promoTimer.current), []);

  // Hitting checkout and then Back restores this page from the browser's bfcache
  // instead of remounting it, so a shipping pick from just before the redirect
  // would otherwise survive and make the next CTA tap skip straight back to
  // checkout instead of reopening the picker. Treat a bfcache restore like a
  // fresh visit — no shipping preselected (same rule as the initial mount).
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) {
        setChosenGroup('');
        setShipPopup(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Remove one product from every carousel (flat lists and device groups alike).
  const dropSuggestion = (pid) => {
    const flat = (list) => (list || []).filter((i) => i && i.id !== pid);
    const grouped = (gs) => (gs || [])
      .map((g) => ({ ...g, items: flat(g.items) }))
      .filter((g) => g.items.length > 0);
    setCrossSells(grouped);
    setCameraProtectors(grouped);
    setOtherBrands(grouped);
    setMagsafe(flat);
    setFreqBought(flat);
    setSimilarCases(flat);
    setUpsells(flat);
    setPhoneAccessories(flat);
  };

  // "Add protection" prompts on case lines: jump to the matching carousel below
  // and flash it, so the shopper sees where the link took them.
  const countItems = (groups) => (groups || []).reduce((n, g) => n + ((g && g.items) ? g.items.length : 0), 0);
  const screenCount = countItems(crossSells);
  const cameraCount = countItems(cameraProtectors);

  // Impressions for the cart-line protection prompts — the denominator for their
  // click-through rate. Once per (product, kind) per page view; a prompt that turns
  // into a tick has already been counted and never fires again.
  const promptSeen = useRef({});
  useEffect(() => {
    if (!cart || !cart.items) return;
    cart.items.forEach((it) => {
      const pr = it.protect;
      if (!pr) return;
      const kinds = [];
      if (!pr.screen && screenCount > 0) kinds.push('screen_protector');
      if (!pr.camera && cameraCount > 0) kinds.push('camera_protector');
      if (pr.back && !pr.back.added) kinds.push('back_protector');
      if (pr.magsafe && !pr.magsafe.added && magsafe.length > 0) kinds.push('magsafe');
      kinds.forEach((kind) => {
        const k = `${it.id}:${kind}`;
        if (promptSeen.current[k]) return;
        promptSeen.current[k] = 1;
        track('cart_protect_prompt_shown', {
          kind, product_id: it.id, device: pr.device || '', discount_pct: pr.pct || '',
        });
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, screenCount, cameraCount, magsafe.length]);

  // Optimistic + debounced quantity: the number updates instantly on each tap,
  // and a single request fires ~550ms after the last tap.
  const flashDone = (key) => {
    setDone((d) => ({ ...d, [key]: true }));
    clearTimeout(doneTimers.current[key]);
    doneTimers.current[key] = setTimeout(() => setDone((d) => { const n = { ...d }; delete n[key]; return n; }), 1500);
  };
  const flushQty = (key, qty) => run(async (seq) => {
    const { ok, data } = await setQty(key, qty, pcRef.current);
    if (latest.current[key] !== qty) return; // superseded by a newer tap on THIS item — its flush wins
    if (!ok) return;
    if (seq === mutSeq.current) {
      // Latest mutation → apply the authoritative cart, THEN drop this item's optimistic
      // pending, so the number reflects the server value (incl. any stock cap).
      apply(data);
      setPending((p) => { const n = { ...p }; delete n[key]; return n; });
    }
    // If superseded by a newer mutation on ANOTHER item, KEEP pending so this number
    // doesn't roll back to the stale cart before the latest cumulative cart lands —
    // apply() clears it once the cart catches up (see the reconcile in apply).
    flashDone(key);
    if (data.notice) flashItemMsg(key, data.notice);
  });
  const step = (key, current, delta, max) => {
    const cap = max && max > 0 ? max : Infinity;
    const wanted = current + delta;
    const next = Math.min(cap, Math.max(1, wanted));
    if (delta > 0 && wanted > cap && cap !== Infinity) flashItemMsg(key, capMessage(cap)); // hit the limit
    if (next === current) return;
    latest.current[key] = next;
    setPending((p) => ({ ...p, [key]: next }));
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => flushQty(key, next), 550);
  };
  // Type a quantity directly. Empty is allowed while typing; on blur it reverts
  // to the last synced value if still empty.
  const typeQty = (key, rawVal, max) => {
    const digits = String(rawVal).replace(/\D/g, '');
    if (digits === '') {
      latest.current[key] = null;
      clearTimeout(timers.current[key]);
      setPending((p) => ({ ...p, [key]: '' }));
      return;
    }
    const cap = max && max > 0 ? max : Infinity;
    const parsed = Math.max(1, parseInt(digits, 10));
    const n = Math.min(cap, parsed);
    if (parsed > cap && cap !== Infinity) flashItemMsg(key, capMessage(cap)); // typed more than allowed
    latest.current[key] = n;
    setPending((p) => ({ ...p, [key]: n }));
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => flushQty(key, n), 700);
  };
  const commitQty = (key) => setPending((p) => (p[key] === '' ? (() => { const n = { ...p }; delete n[key]; return n; })() : p));
  const remove = (key) => {
    clearTimeout(timers.current[key]);
    const gone = cart && cart.items ? cart.items.find((i) => i.key === key) : null;
    let emptied = false;
    setCart((c) => {
      if (!c) return c;
      const items = c.items.filter((i) => i.key !== key); // optimistic, immediate
      emptied = items.length === 0;
      return { ...c, items };
    });
    // Last line gone → no suggestions can still apply; don't wait for the refresh.
    if (emptied) { setCrossSells([]); setCameraProtectors([]); setMagsafe([]); setFreqBought([]); setSimilarCases([]); setOtherBrands([]); setUpsells([]); setPhoneAccessories([]); }
    track('cart_item_removed', {
      product_id: gone ? gone.id : null, product_name: gone ? gone.name : '',
      qty: gone ? gone.qty : null, value_minor: gone ? gone.valueMinor : null,
      cart_emptied: emptied,
    });
    return run(async (seq) => {
      const { ok, data } = await setQty(key, 0, pcRef.current);
      if (ok) applyFresh(seq, data); // apply() schedules the suggestion refresh
    });
  };

  // The promo carousels sit well below the cart, so an add down there lands
  // off-screen. Bring the shopper back up to see the line arrive and the totals
  // move. Deferred a frame: applyFresh() has only just queued the re-render, and
  // retiring the pressed card reflows the carousel underneath us.
  const scrollToCart = () => {
    window.setTimeout(() => {
      const el = document.querySelector('.cart-title') || document.querySelector('.cart-shell');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const add = (cs, device, section) => {
    const pid = cs.id;
    setAdding((a) => ({ ...a, [pid]: true })); // immediate feedback on the pressed card
    return run(async (seq) => {
    try {
      const { ok, data } = await addToCart(pid, 1, device);
      if (ok) {
        applyFresh(seq, data); // item + totals update live in place — no modal needed
        // The response no longer carries rebuilt carousels, so retire the card here.
        // Cut-to-fit films are the exception: one is bought per device, so it stays
        // on offer until the background refresh says every cart model is covered.
        if (!cs.universal) dropSuggestion(pid);
        // Usage: a product was added FROM a specific promo block (screen/camera/etc).
        // `section` is also what distinguishes a carousel add (far down the page)
        // from the one-tap back-cover add on the cart line itself, which is
        // already in view and must not move.
        if (section) {
          track('cart_crosssell_add', {
            section, product_id: pid, product_name: cs.name,
            universal: !!cs.universal, device: device || '',
          });
          scrollToCart();
        }
      }
    } finally {
      setAdding((a) => { const n = { ...a }; delete n[pid]; return n; });
    }
    });
  };

  const chooseGroup = (group) => {
    setChosenGroup(group); // optimistic selection
    setShipErr('');
    return run(async (seq) => {
      const { ok, data } = await selectShipping(group, pcRef.current);
      if (ok) applyFresh(seq, data);
    });
  };

  const refreshShipping = () => run(async (seq) => {
    const { ok, data } = await cartShipping(pcRef.current);
    if (ok) applyFresh(seq, data);
  });

  const submitCoupon = (e) => {
    e.preventDefault();
    if (!coupon.trim()) return;
    setCouponErr('');
    return run(async (seq) => {
      setCouponBusy(true);
      const { ok, data } = await applyCoupon(coupon.trim());
      setCouponBusy(false);
      if (ok) { applyFresh(seq, data); setCoupon(''); } else setCouponErr(data.message || 'Μη έγκυρο κουπόνι.');
    });
  };

  const dropCoupon = (code) => run(async (seq) => {
    setCouponBusy(true);
    const { ok, data } = await removeCoupon(code);
    setCouponBusy(false);
    if (ok) applyFresh(seq, data);
  });

  if (!cart || !(cart.items || []).length) {
    return (
      <div className="cart-layout">
        <div className="cart-empty">
          <RestoreNotice />
          <p>Το καλάθι σου είναι άδειο.</p>
          <a className="cn-btn cn-btn--primary" href={cfg.shopUrl || '/'}>Συνέχισε τις αγορές</a>
        </div>
      </div>
    );
  }

  const teaser = cart.boxnow;
  const groups = (shipping && shipping.groups) || [];
  // Server-side explanation for a group that came back «Μη διαθέσιμο» for the whole
  // cart (today: over the courier weight cap → pickup only). Never invent the copy
  // here — the rule and the wording both live in PHP.
  const shipNote = (shipping && shipping.note) || '';
  // Per-group «why unavailable» (g.reason, e.g. the pickup minimum) opens on tap;
  // desktop hover shows it via CSS. Kept out of the list so it doesn't clutter the cart.
  const [whyOpen, setWhyOpen] = useState('');
  const chosen = chosenGroup; // no default — user must actively pick one

  // Carry the chosen shipping group to the new checkout so it opens preselected.
  const baseCheckout = cfg.checkoutUrl || '/checkout-next/';
  const checkoutHref = chosen
    ? baseCheckout + (baseCheckout.indexOf('?') === -1 ? '?' : '&') + 'ship=' + encodeURIComponent(chosen)
    : baseCheckout;

  const isMobileView = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 900px)').matches;

  // Block checkout until a shipping method is chosen. On mobile the selector is
  // hidden in the cart, so proceeding opens a popup to pick it; on desktop we show
  // the inline red validation.
  // A line can sell out (backorders=no) while sitting in the cart. The server blocks
  // the order in cn_place_order; here we stop the shopper at the cart so they can
  // remove it first, and disable the checkout CTAs.
  const stockError = (cart && cart.stockError) || '';

  const goCheckout = (e) => {
    if (syncing > 0) { e.preventDefault(); return; } // cart still syncing (e.g. mid-delete) — don't proceed with a stale cart
    if (stockError) {
      e.preventDefault();
      const el = document.querySelector('.cart-stockwarn');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (chosen) return; // let the link navigate
    e.preventDefault();
    if (isMobileView()) {
      setShipErr('');
      setShipPopup(true);
    } else {
      setShipErr('Επίλεξε τρόπο αποστολής για να συνεχίσεις.');
      const el = document.querySelector('.cart-ship');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const jumpToPromo = (section, ctx) => {
    const el = document.getElementById(`cx-${section}`);
    track('cart_protect_prompt_click', { kind: section, found: !!el, ...ctx });
    if (!el) return;
    if (el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('is-flash');
    setTimeout(() => el.classList.remove('is-flash'), 1800);
  };
  const protectDone = (label) => (<span className="cart-item__protect-done"><span aria-hidden="true">✓</span> {label}</span>);
  // One prompt line: a tick once that protection is in the cart, otherwise a link
  // down to the carousel — and only while that carousel actually has something.
  const protectLine = (kind, has, count, pct, label, linkText, ctx) => {
    if (has) return protectDone(label);
    if (count < 1) return null;
    return (
      <button type="button" className="cart-item__protect-link" onClick={() => jumpToPromo(kind, ctx)}>
        {/* Kept short on purpose: these have to hold ONE line down to a 360px
            phone, and «με -15% έκπτωση» wrapped every prompt onto two. "-15%"
            already reads as a discount without the extra two words. */}
        + Προσθέστε {linkText || label.toLowerCase()}{pct ? ` -${pct}%` : ''}
      </button>
    );
  };
  // Back-cover film — no carousel to scroll to: one tap adds it, cut for this line's model.
  const backLine = (back, pct, deviceSlug, ctx) => {
    if (!back) return null;
    if (back.added) return protectDone('Προστασία πίσω όψης');
    const busy = !!adding[back.id];
    return (
      <button
        type="button"
        className={`cart-item__protect-link ${busy ? 'is-adding' : ''}`}
        disabled={busy}
        onClick={() => {
          track('cart_protect_prompt_click', { kind: 'back_protector', oneClick: true, ...ctx });
          add({ id: back.id, name: back.name, universal: true }, deviceSlug || undefined, 'back_protector');
        }}
      >
        {busy ? (<><span className="cart-spinner cart-item__protect-spin" aria-hidden="true" />Προστίθεται…</>)
          // NBSP before the price so it never wraps onto a line of its own. Same
          // shortened discount wording as the other prompts — this is the longest
          // of the four and has to hold one line at 360px too.
          : `+ Προσθέστε προστασία πίσω όψης${pct ? ` -${pct}%` : ''} (${back.price})`}
      </button>
    );
  };

  // Proceed to checkout with a given shipping group — used by the mobile popup's
  // own CTA. Picking an option there used to navigate on the spot; it no longer
  // does (see the popup's button for why).
  const goToCheckoutWith = (id) => {
    if (syncing > 0 || stockError) return;
    window.location.href = baseCheckout + (baseCheckout.indexOf('?') === -1 ? '?' : '&') + 'ship=' + encodeURIComponent(id);
  };

  // Single shipping-option row — reused inline (desktop) and in the mobile popup.
  const shipOpt = (g, groupName) => (
    <label
      key={g.id}
      className={`cart-ship__opt cart-ship__opt--${g.id} ${chosen === g.id ? 'is-selected' : ''} ${!g.available ? 'is-unavailable' : ''} ${!g.available && g.reason ? 'has-why' : ''} ${whyOpen === g.id ? 'is-why-open' : ''}`}
      onClick={!g.available && g.reason ? (e) => { e.preventDefault(); setWhyOpen(whyOpen === g.id ? '' : g.id); } : undefined}
    >
      <input
        type="radio"
        name={groupName}
        checked={chosen === g.id}
        disabled={!g.available}
        onChange={() => {
          if (!g.available) return;
          chooseGroup(g.id);
        }}
      />
      <span className="cart-ship__icon"><ShipIcon id={g.id} /></span>
      <span className="cart-ship__label">{g.label}</span>
      <span className="cart-ship__cost">
        {g.available ? (
          g.free ? <strong className="is-free">Δωρεάν</strong> : (<>{g.from && <em className="cart-ship__from">Από</em>}{' '}{g.cost || '—'}</>)
        ) : <span className="cart-ship__na">Μη διαθέσιμο{g.reason && <svg className="cart-ship__why-i" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M8 7v4.5M8 4.6v.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>}</span>}
      </span>
      {!g.available && g.reason && <span className="cart-ship__why" role="tooltip">{g.reason}</span>}
    </label>
  );

  return (
    <div className="cart-shell">
      {/* Title sits ABOVE the two-column layout so the right column's first box
          starts level with the left column's first box, not with the heading. */}
      <h1 className="cart-title">Το καλάθι σου</h1>
      <div className="cart-layout">
      <main className="cart-main">
        <RestoreNotice />
        {teaser && !teaser.free && teaser.remaining && (
          <div className="cart-teaser">
            <div className="cart-teaser__txt">Πρόσθεσε ακόμη <strong>{teaser.remaining}</strong> για <strong>δωρεάν μεταφορικά</strong> με <span className="cart-bn">Box Now</span>!</div>
            <div className="cart-teaser__bar"><span style={{ width: `${teaser.progress}%` }} /></div>
          </div>
        )}
        {teaser && teaser.free && (
          <div className="cart-teaser is-free"><div className="cart-teaser__txt">Κέρδισες <strong>δωρεάν μεταφορικά</strong> με <span className="cart-bn">Box Now</span>!</div></div>
        )}

        {cfg.coins && cfg.coins.points > 0 && (
          <a
            className="cart-coins"
            href={cfg.coins.url || '/my-account/loyalty_reward/'}
            onClick={(e) => {
              // Open WPLoyalty's "My Coins" modal in place if it's available; else
              // follow the link to the loyalty rewards page.
              try {
                if (window.wlr_jquery && window.wlr_jquery('.wlr-my-rewards-sections').length) {
                  e.preventDefault();
                  window.wlr_jquery('body').trigger('wlr_my_reward_section', ['rewards']);
                }
              } catch (err) { /* fall through to the link */ }
            }}
          >
            <span>Έχεις μαζέψει <strong>{Number(cfg.coins.points).toLocaleString('el-GR')} {cfg.coins.label}</strong></span>
            <span className="cart-coins__cta">Δες τα ›</span>
          </a>
        )}

        {stockError && (
          <div className="cart-stockwarn" role="alert">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
            <span>{stockError}</span>
          </div>
        )}

        <ul className="cart-items">
          {cart.items.map((it, i) => {
            const raw = pending[it.key];
            const updating = raw != null;
            const showTick = !updating && done[it.key];
            const display = raw != null ? raw : it.qty;          // may be '' while typing
            const numQty = raw != null && raw !== '' ? raw : it.qty;
            const atMax = it.max && it.max > 0 && numQty >= it.max;
            return (
              <li className={`cart-item ${updating ? 'is-updating' : ''} ${it.outOfStock ? 'is-oos' : ''}`} key={it.key || i}>
                <a className="cart-item__thumb" href={it.url || '#'}>
                  {it.image ? <img src={it.image} alt="" loading="lazy" /> : null}
                </a>
                <span className="cart-item__info">
                  <a className="cart-item__name" href={it.url || '#'}>{it.name}</a>
                  {it.outOfStock && <span className="cart-item__oos">Εξαντλήθηκε — αφαίρεσέ το για να συνεχίσεις</span>}
                  {it.cutFor && <span className="cart-item__cutfor">Κοπή για: <strong>{it.cutFor}</strong></span>}
                  {/* Laser engraving (mu-plugin 114): what goes on the piece, one row per side / name list. */}
                  {it.engrave && it.engrave.lines && it.engrave.lines.map((l, j) => (
                    <span className="cart-item__cutfor cart-item__engrave" key={j}>{l.k}: <strong>{l.v}</strong></span>
                  ))}
                  <span className="cart-item__price">
                    {it.regularTotal && <del className="cart-item__was">{it.regularTotal}</del>}
                    {it.total}
                  </span>
                  {/* The line's own delivery date — the same estimate, for the same
                      destination, that this product's page quotes. `prefix` is the
                      «Εκτιμώμενη παράδοση / παραλαβή» wording; `label` is the dated
                      phrase, prepositions included («από … έως …», «την …»), so the
                      Greek is built in one place server-side. */}
                  {it.delivery && it.delivery.label && (
                    <span className="cart-item__eta">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 3h13v11H1zM14 7h4l3 3v4h-7z" /><circle cx="6" cy="18" r="2" /><circle cx="17.5" cy="18" r="2" /></svg>
                      {it.delivery.prefix && <span className="cart-item__eta-note">{it.delivery.prefix}</span>}
                      <strong>{it.delivery.label}</strong>
                    </span>
                  )}
                </span>
                {it.engrave && it.engrave.fixedQty ? (
                  /* One engraved piece per name on the list: the list is the quantity. */
                  <span className="cart-item__qty cart-item__qty--fixed" aria-label={`Ποσότητα ${it.qty}, ένα κομμάτι για κάθε όνομα`}>× {it.qty}</span>
                ) : (
                <span className="cart-item__qty">
                  <button type="button" aria-label="Μείωση" disabled={numQty <= 1} onClick={() => step(it.key, numQty, -1, it.max)}>−</button>
                  <input type="text" inputMode="numeric" className="cart-item__qtynum" aria-label="Ποσότητα"
                    value={display} onChange={(e) => typeQty(it.key, e.target.value, it.max)} onBlur={() => commitQty(it.key)} />
                  <button type="button" aria-label="Αύξηση" className={atMax ? 'is-maxed' : ''} onClick={() => step(it.key, numQty, +1, it.max)}>+</button>
                </span>
                )}
                <button type="button" className="cart-item__remove" aria-label="Αφαίρεση" onClick={() => remove(it.key)}>✕</button>
                {/* Protection prompts sit on their own row, bottom-right of the line. */}
                {it.protect && (() => {
                  const ctx = { product_id: it.id, device: it.protect.device || '', discount_pct: it.protect.pct || '' };
                  const s = protectLine('screen_protector', it.protect.screen, screenCount, it.protect.pct, 'Προστασία οθόνης', '', ctx);
                  const c = protectLine('camera_protector', it.protect.camera, cameraCount, it.protect.pct, 'Προστασία κάμερας', '', ctx);
                  const b = backLine(it.protect.back, it.protect.pct, it.protect.deviceSlug, ctx);
                  const m = it.protect.magsafe
                    ? protectLine('magsafe', it.protect.magsafe.added, magsafe.length, '', 'Αξεσουάρ MagSafe', 'αξεσουάρ MagSafe', ctx)
                    : null;
                  return (s || c || b || m) ? <span className="cart-item__protect">{s}{c}{b}{m}</span> : null;
                })()}
                {updating && <span className="cart-spinner cart-item__spin" aria-label="Ενημέρωση…" />}
                {showTick && <span className="cart-item__tick" aria-hidden="true">✓</span>}
                {itemMsg[it.key] && <span className="cart-item__msg" role="status">{itemMsg[it.key]}</span>}
              </li>
            );
          })}
        </ul>
      </main>

      <aside className="cart-aside">
        {/* Estimated delivery — its own wrapper, above the order summary. The
            postcode line sits on top; the delivery estimate + on-time pill below. */}
        {delivery && delivery.label && (
          <div className="cart-eta">
            <div className="cart-eta__zip">
              {zipOpen ? (
                <form className="cart-inline-form" onSubmit={(e) => { e.preventDefault(); if ((postcode.match(/\d/g) || []).length < 4) return; refreshShipping(); setZipOpen(false); }}>
                  <input type="text" inputMode="numeric" maxLength={5} placeholder="Τ.Κ. για ακριβή τιμή & παράδοση" value={postcode} autoFocus onChange={(e) => setPostcode(e.target.value)} />
                  <button type="submit" className="cn-btn cn-btn--ghost" disabled={(postcode.match(/\d/g) || []).length < 4}>Υπολογισμός</button>
                </form>
              ) : postcode ? (
                <div className="cart-zip-applied">
                  <span className="cart-zip-applied__txt">Τιμές &amp; παράδοση για Τ.Κ. <strong>{prettyZip(postcode)}</strong></span>
                  <button type="button" className="cart-linkdotted cart-linkdotted--muted" onClick={() => setZipOpen(true)}>Αλλαγή</button>
                </div>
              ) : (
                <button type="button" className="cart-linkdotted cart-linkdotted--muted" onClick={() => setZipOpen(true)}>Υπολόγισε ακριβή μεταφορικά με Τ.Κ.</button>
              )}
            </div>
            <div className="cart-eta__main">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 3h13v11H1zM14 7h4l3 3v4h-7z" /><circle cx="6" cy="18" r="2" /><circle cx="17.5" cy="18" r="2" /></svg>
              <span className="cart-eta__body">
                <span className="cart-eta__label">{delivery.prefix || 'Εκτιμώμενη παράδοση'}</span>
                <strong className="cart-eta__val">{delivery.label}</strong>
              </span>
              {cfg.onTime && cfg.onTime.pct != null && cfg.onTime.total >= 100 && (
                <span className="cart-ontime cart-tip" tabIndex={0}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>
                  <strong>{Math.round(cfg.onTime.pct)}%</strong> εγκαίρως
                  <span className="cart-tip__bubble" role="tooltip">Υπολογισμένο από στατιστικά δεδομένα με βάση τις παραδόσεις παραγγελιών των τελευταίων 3 μηνών.</span>
                </span>
              )}
            </div>
          </div>
        )}

      <div className="cart-summary">
        <h2 className="cart-summary__title">Παράδοση{syncing > 0 && <span className="cart-spinner" aria-label="Ενημέρωση…" />}</h2>

        <div className={`cart-ship ${shipErr ? 'is-invalid' : ''}`}>
          {groups.map((g) => shipOpt(g, 'cart-ship'))}
          {shipNote && <p className="cart-ship__note">{shipNote}</p>}
          {shipErr && <p className="cart-ship__err" role="alert">{shipErr}</p>}
        </div>

        <div className="cart-coupon">
          {(cart.coupons || []).map((c) => (
            <div className="cart-coupon__applied" key={c.code}>
              <span className="cart-coupon__tag">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>
                {c.code} <span className="cart-coupon__amt">−{c.amount}</span>
              </span>
              <button type="button" className="cn-link" disabled={couponBusy} onClick={() => dropCoupon(c.code)}>Αφαίρεση</button>
            </div>
          ))}
          {couponOpen ? (
            <>
              <form className="cart-coupon__form" onSubmit={submitCoupon}>
                <input type="text" placeholder="Κουπόνι ή δωροκάρτα" value={coupon} autoFocus onChange={(e) => setCoupon(e.target.value)} />
                <button type="submit" className="cn-btn cn-btn--ghost" disabled={couponBusy || !coupon.trim()}>Εφαρμογή</button>
              </form>
              {couponErr && <p className="cart-coupon__err">{couponErr}</p>}
            </>
          ) : (
            <button type="button" className="cart-linkdotted" onClick={() => setCouponOpen(true)}>Έχεις κουπόνι ή δωροκάρτα;</button>
          )}
        </div>

        <dl className={`cn-sum__totals ${syncing > 0 ? 'is-updating' : ''}`}>
          {/* When anything is discounted (offers/coupons), show a combined
              savings line so the deal is still visible. */}
          {cart.savings && <div className="cn-sum__discount"><dt>Έκπτωση</dt><dd>−{cart.savings}</dd></div>}
          <div><dt>Μεταφορικά</dt><dd>{chosen ? (cart.shipping || 'Δωρεάν') : <span className="cart-ship__pending">Επίλεξε τρόπο</span>}</dd></div>
          {(cart.fees || []).map((f, i) => (<div key={i}><dt>{f.name}</dt><dd>{f.amount}</dd></div>))}
        </dl>
        <div className="cn-sum__grand"><span>Σύνολο</span><span>{chosen ? cart.total : (cart.totalNoShip || cart.total)}</span></div>
        <a className={`cn-btn cn-btn--primary cart-checkout ${chosen && syncing === 0 && !stockError ? '' : 'is-disabled'}`} href={checkoutHref} aria-disabled={!chosen || syncing > 0 || !!stockError} onClick={goCheckout}>Συνέχεια στο ταμείο <span className="cart-cta__arrow" aria-hidden="true">→</span></a>

        {/* Express pay (Apple Pay / Google Pay / Link) — the button + flow are
            mounted by the existing express-checkout script (mu-plugin 15) into
            these containers. Hidden until a wallet is detected. */}
        <div id="admexp-express-wrap" className="cart-express" style={{ display: 'none' }}>
          <div className="cart-express__or"><span>ή άμεση πληρωμή με Apple&nbsp;Pay / Google&nbsp;Pay / Link</span></div>
          <div id="admexp-payment-request-button" />
          <p className="cart-express__consent">Με την ολοκλήρωση της πληρωμής αποδέχεσαι τους όρους χρήσης.</p>
          <div id="admexp-status" style={{ display: 'none' }} />
        </div>
      </div>
      </aside>

      {/* Full-screen processing overlay used by the express-checkout flow. */}
      <div id="admexp-overlay" className="cart-express__overlay" aria-hidden="true" style={{ display: 'none' }}>
        <div id="admexp-spinner" className="cart-express__spinner" />
        <div id="admexp-overlay-msg">Ολοκλήρωση παραγγελίας…</div>
        <div id="admexp-overlay-sub">Μην κλείσεις ούτε ανανεώσεις τη σελίδα.</div>
      </div>
      </div>

      {/* Cart-based promotions — full container width, below the cart + summary. */}
      {(crossSells.length > 0 || cameraProtectors.length > 0 || magsafe.length > 0 || freqBought.length > 0 || similarCases.length > 0 || otherBrands.length > 0 || upsells.length > 0 || phoneAccessories.length > 0) && (
        <section className="cart-promos">
          <h2 className="cart-promos__heading">{promoMeta.heading || 'Προσφορές για το καλάθι σου'}</h2>
          <CrossSells
            groups={crossSells}
            adding={adding}
            onAdd={add}
            section="screen_protector"
            eyebrow="Προτεινόμενα για σένα"
            title={(promoMeta.cross && promoMeta.cross.title) || 'Πρόσθεσε προστασία οθόνης'}
            offer={promoMeta.cross && promoMeta.cross.offer}
            moreUrl={promoMeta.cross && promoMeta.cross.url}
          />
          <CrossSells
            groups={cameraProtectors}
            adding={adding}
            onAdd={add}
            section="camera_protector"
            eyebrow="Προτεινόμενα για σένα"
            title={(promoMeta.camera && promoMeta.camera.title) || 'Πρόσθεσε προστασία κάμερας'}
            offer={promoMeta.camera && promoMeta.camera.offer}
            moreUrl={promoMeta.camera && promoMeta.camera.url}
          />
          <CrossSells
            items={magsafe}
            adding={adding}
            onAdd={add}
            section="magsafe"
            eyebrow="Η θήκη σου υποστηρίζει MagSafe"
            title={(promoMeta.magsafe && promoMeta.magsafe.title) || 'Αξεσουάρ MagSafe για τη συσκευή σου'}
            moreUrl={promoMeta.magsafe && promoMeta.magsafe.url}
          />
          <CrossSells
            items={freqBought}
            adding={adding}
            onAdd={add}
            section="frequently_bought"
            eyebrow="Με βάση τις αγορές πελατών"
            title="Πελάτες επίσης αγόρασαν"
          />
          <CrossSells
            items={similarCases}
            adding={adding}
            onAdd={add}
            section="similar_cases"
            eyebrow="Μπορεί να σου αρέσουν"
            title={(promoMeta.similar && promoMeta.similar.title) || 'Παρόμοιες θήκες'}
            offer={promoMeta.similar && promoMeta.similar.offer}
            moreUrl={promoMeta.similar && promoMeta.similar.url}
          />
          <CrossSells
            groups={otherBrands}
            adding={adding}
            onAdd={add}
            section="other_brands"
            eyebrow="Επιπλέον επιλογές για τη συσκευή σας"
            title="Παρόμοια προϊόντα από άλλους κατασκευαστές"
          />
          <CrossSells
            items={upsells}
            adding={adding}
            onAdd={add}
            section="upsells"
            eyebrow="Επιλεγμένα για σένα"
            title="Μπορεί επίσης να σου αρέσουν"
          />
          {/* Closing block: more from the «Αξεσουάρ κινητών» tree, shown only when
              the cart already holds something from it. Always the last carousel. */}
          <CrossSells
            items={phoneAccessories}
            adding={adding}
            onAdd={add}
            section="phone_accessories"
            eyebrow="Συμπλήρωσε το καλάθι σου"
            title={(promoMeta.phoneAcc && promoMeta.phoneAcc.title) || 'Αξεσουάρ κινητών'}
            moreUrl={promoMeta.phoneAcc && promoMeta.phoneAcc.url}
          />
        </section>
      )}

      {/* Sticky mobile checkout bar — total + CTA reachable from anywhere. */}
      <div className="cart-mobilebar">
        <div className="cart-mobilebar__info">
          <span className="cart-mobilebar__label">Σύνολο</span>
          <span className="cart-mobilebar__total">{chosen ? cart.total : (cart.totalNoShip || cart.total)}</span>
        </div>
        <a className={`cn-btn cn-btn--primary cart-mobilebar__cta ${chosen && syncing === 0 && !stockError ? '' : 'is-disabled'}`} href={checkoutHref} aria-disabled={!chosen || syncing > 0 || !!stockError} onClick={goCheckout}>Συνέχεια στο ταμείο <span className="cart-cta__arrow" aria-hidden="true">→</span></a>
      </div>

      {/* Mobile-only: pick the shipping method in a popup after tapping proceed. */}
      {shipPopup && (
        <div className="cart-shippop" role="dialog" aria-modal="true" onClick={() => setShipPopup(false)}>
          <div className="cart-shippop__box" onClick={(e) => e.stopPropagation()}>
            <div className="cart-shippop__head">
              <span>Τρόπος αποστολής</span>
              <button type="button" className="cart-shippop__close" aria-label="Κλείσιμο" onClick={() => setShipPopup(false)}>✕</button>
            </div>
            <div className="cart-shippop__opts">
              {groups.map((g) => shipOpt(g, 'cart-ship-pop'))}
              {shipNote && <p className="cart-ship__note">{shipNote}</p>}
            </div>
            {/* The sheet has its own CTA, and choosing an option no longer navigates
                by itself. It used to: this panel rises from the BOTTOM of the screen,
                exactly where the finger that opened it already was, and its last row
                is «Παραλαβή από το κατάστημα — Δωρεάν». One stray tap there put an
                order on store pickup, and checkout then offered no courier option and
                (on an απόδειξη) no address field at all, so nothing looked wrong until
                the parcel was sitting in our locker — order 1617618, Κασσάνδρεια.
                Two taps now, and the bottom strip of the screen holds a button that
                does nothing until a method has actually been selected. */}
            <button
              type="button"
              className={`cn-btn cn-btn--primary cart-shippop__cta ${chosen && syncing === 0 && !stockError ? '' : 'is-disabled'}`}
              disabled={!chosen || syncing > 0 || !!stockError}
              onClick={() => goToCheckoutWith(chosen)}
            >
              {!chosen ? 'Διάλεξε τρόπο αποστολής' : (
                <>
                  Συνέχεια στο ταμείο
                  {syncing > 0
                    ? <span className="cart-spinner" aria-label="Ενημέρωση…" />
                    : <span className="cart-cta__arrow" aria-hidden="true">→</span>}
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
